import React, { useEffect, useState } from 'react';
import axios from 'axios';
import get from 'lodash/get';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import withContext from '../common/withContext';
import ProjectContext from './projectContext';
import ProjDetailCertAlert from './projDetailCertAlert';
import Confirmation from '../common/confirmation';
import { setLoading, useApplicationState } from '../../utilities/applicationState';

import './credentials.scss';
import {
	SdfBox,
	SdfInput,
	SdfButton,
	SdfIcon,
	SdfShimmer,
	SdfSwitch,
	SdfTooltip,
	SdfAlert,
	SdfLink,
	SdfPromoBox,
	SdfBusyIndicator
} from '@synerg/react-components';
import { uniqueId } from 'lodash';
import Toast from '../common/toast';
import ADPLoginFlowSVG from '../../images/adp-login-flow.svg?react';


function EndUser({ context, setSelectedTab, setEndUserApiTabActive, allCertsInfo }) {
	const [clientSecret, setClientSecret] = useState('');
	const [clientID, setClientID] = useState(null);
	const [clientIdLoading, setClientIdLoading] = useState(false);
	const [clientSecretLoading, setClientSecretLoading] = useState(false);
	const [showSecret, setShowSecret] = useState(false);
	const [redirectUri, setRedirectUri] = useState('');
	const [isRedirectUriBusy, setIsRedirectUriBusy] = useState(false);
	const [redirectUriPrime, setRedirecturiPrime] = useState('');
	const [openRedirectConfirmation, setOpenRedirectConfirmation] = useState(false);
	const [openRegenerateConfirmation, setOpenRegenerateConfirmation] = useState(false);
	const [toast, setToast] = useState(false);
	const { projectDetail } = context;
	const [useCases, setUseCases] = useState([]);
	const { type, caseDocumentation, caseID } = projectDetail;
	const matchedUseCase = useCases.find(useCase => useCase._id === caseID) || {};
	const { documentation } = matchedUseCase;
	const firstDocument = caseDocumentation ? caseDocumentation[0] : null;
	const useCaseDocument = documentation ? documentation[0] : null;
	const isDev = type === 'DEV';
	const [{ loading }, dispatch] = useApplicationState();

	const handleResponse = (message, status = "error") => {
		setToast({
			key: uniqueId(),
			status: status,
			message: message,
			showToast: true
		});
	};


	const onShowSecret = event => {
		if (event.detail) {
			setShowSecret(true);
		}
		else {
			setClientSecret('');
			setShowSecret(false);
		}
	};

	const onCancelRedirect = () => {
		setRedirectUri(redirectUriPrime);
	}


	// get application call
	useEffect(() => {
		async function getApplicationDetails(projectID, consumerApplicationID) {
			dispatch(setLoading(true));
			try {
				const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/consumer-application`, {
					params: { projectID, consumerApplicationID }
				});
				const { applicationRedirectLink: { href } = {} } = data;
				setRedirectUri(href);
				setRedirecturiPrime(href);
			} catch (e) {
				const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get consumer application details. Please try again.');
				handleResponse(errorMessage);
			}
			dispatch(setLoading(false));
		}

		const { _id: projectId, b2cConsumerAppID } = projectDetail;
		if (projectId && b2cConsumerAppID) {
			getApplicationDetails(projectId, b2cConsumerAppID);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectDetail]);

	// get credentials call
	useEffect(() => {
		async function getApplicationCredentials(projectID, consumerApplicationID) {
			setClientIdLoading(true)
			try {
				const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/client-id`, {
					params: { projectID, consumerApplicationID }
				});
				setClientID(data.clientID);
				setClientIdLoading(false);
			} catch (e) {
				setClientIdLoading(true);
				const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get consumer application details. Please try again.');
				handleResponse(errorMessage);
			}
		}

		const { _id: projectId, b2cConsumerAppID } = projectDetail;
		if (projectId && b2cConsumerAppID) {
			getApplicationCredentials(projectId, b2cConsumerAppID);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectDetail]);



	// regenerate credentials
	const regenerateCredentials = async () => {
		setClientSecretLoading(true);
		try {
			const { _id: projectID, b2cConsumerAppID: consumerApplicationID } = projectDetail;
			const { data: { clientSecret } } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/regenerate`, {
				projectID, consumerApplicationID
			});
			setClientSecret(clientSecret);
			setClientSecretLoading(false);
		} catch (e) {
			setClientSecret('');
			setClientSecretLoading(false);
			const errorMessage = get(e, 'response.data.message', 'Something went wrong while trying to regenerate credentials. Please try again.');
			handleResponse(errorMessage);
		}
	};

	// get client secret call
	useEffect(() => {
		async function getProjectCredentials(projectID, consumerApplicationID) {
			setClientSecretLoading(true);
			try {
				const { data: { clientSecret } } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/client-secret`, {
					params: { projectID, consumerApplicationID }
				});
				setClientSecret(clientSecret);
				setClientSecretLoading(false);
			} catch (e) {
				setClientSecret('');
				setClientSecretLoading(false);
				const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get project credentials. Please try again.');
				handleResponse(errorMessage);
			}
		}

		const { _id: projectId, b2cConsumerAppID } = projectDetail;
		if (projectId && b2cConsumerAppID && showSecret) {
			getProjectCredentials(projectId, b2cConsumerAppID);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showSecret, projectDetail]);

	// get use cases
	useEffect(() => {
		async function getProjectUseCases() {
			try {
				const { data } = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/project/use-cases`);
				setUseCases(data);
			} catch (e) {
				const errorMessage = get(e, 'response.data.message', 'Error occured when trying to get project use cases. Please try again.');
				handleResponse(errorMessage);
			}
		}

		getProjectUseCases();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateRedirectUri = async () => {
		setIsRedirectUriBusy(true);
		try {
			const { _id: projectID, b2cConsumerAppID: consumerApplicationID } = projectDetail;
			const { data: { applicationRedirectLink } } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project/redirect-url`, {
				projectID, consumerApplicationID, redirectURL: redirectUri
			});
			setRedirectUri(applicationRedirectLink);
			setRedirecturiPrime(applicationRedirectLink);
			setIsRedirectUriBusy(false);
			handleResponse('App Redirect Uri Updated Successfully', 'success');
		} catch (e) {
			setRedirectUri(redirectUriPrime);
			setIsRedirectUriBusy(false);
			const errorMessage = get(e, 'response.data.message', 'Something went wrong while trying to update redirect uri. Please try again.');
			handleResponse(errorMessage);
		}
	};

	const titleForMarketplace = isDev
		? 'Getting Started with your End-user/SSO Credentials'
		: 'Getting End-user/SSO credentials ready for production';

	const subtitle = isDev
		? 'Follow the steps to enable users to SSO to your application using ADP Credentials as well as to retrieve the specific logged in users ADP information.'
		: 'Follow these steps to update your application to get ready for a production environment.';



	return (<div className="data-connector mt-12">

		{toast && <Toast
			key={toast.key}
			status={toast.status}
			message={toast.message}
			showToast={toast.showToast}
		/>}

		<div>

			<div className="text-heading-06 font-black leading-default">{titleForMarketplace}</div>
			<div className="mt-2 step-container text-body-03">{subtitle}</div>

			<div className="mt-12 step-container text-heading-05 leading-sm font-bold">
				Step 1<br></br>
				{isDev ? "Provide redirect URI of your app" : "Provide production redirect URI of your app"}</div>

			<div className="step-container mt-4">
				<div className="h-full">
					<SdfBox
						shape="rounded"
						variant="bordered"
						spacing="loose"
						className="px-8 pb-8 h-full"
					>
						<div>
							<div className="text-body-02">End-user logs in to ADP from your app and provides one-time consent, and ADP will send the user back to your supplied app redirect URI (see below) with an authorization code.</div>

							<div className="mt-8 text-body-02">Since ADP is the identity provider for your end-user consumer application, please provide an app redirect URI to indicate where to send user after successful login.</div>

							{isDev && <SdfAlert status="warning" className="mt-8">
								<div className="mr-2 text-base">
									You must update the app redirect URI when you switch environments prior to going live
								</div>
							</SdfAlert>}
						</div>

						<div className="mt-8 flex justify-between items-end flex-wrap gap-y-2">
							<SdfInput style={{ width: "70%" }} className="mr-3" placeholder="App redirect URI" label="App redirect URI" value={redirectUri} {...(isRedirectUriBusy || loading) && { disabled: true }} onSdfInput={(event) => setRedirectUri(event.detail)}></SdfInput>

							<SdfButton {...(isRedirectUriBusy || loading) && { disabled: true }} onClick={() => setOpenRedirectConfirmation(true)}>
								<div style={{ height: '1.625rem' }} className="px-3">
									Update redirect
									{isRedirectUriBusy && <SdfBusyIndicator className='relative' style={{ bottom: '1.25rem' }} size='sm' />}
								</div>
							</SdfButton>
						</div>
						<div className="text-sm mt-2" style={{ opacity: "0.75", width: "71%" }}>{isDev ? "Using localhost is acceptable while app is in development." : "Please make sure this is the production environment application url."}</div>

						{isDev && <SdfLink className="mt-12" href="https://developers.adp.com/build/guides/marketplace-integration-guides/authorization-process-end-user-apps" target="_blank" icon="external-link"> <SdfIcon icon="external-link" /> View authorization process (guide) </SdfLink>}
					</SdfBox>

					{openRedirectConfirmation && <Confirmation setOpenConfirmation={setOpenRedirectConfirmation} confirmationMessage={"Updating your redirect URI may cause your existing integration to break. Click Yes if you want to proceed."} onConfirm={updateRedirectUri} onCancel={onCancelRedirect} />}

				</div>

				{isDev && <div className="h-full">
					<SdfBox
						shape="rounded"
						variant="bordered"
						spacing="tight"
						className="pt-6 h-full"
					>
						<div className="text-heading-06 font-black leading-default text-center">ADP uses standard OAuth 2.0 flow</div>
						<div className="flex justify-center mt-8 mb-6"><ADPLoginFlowSVG /></div>

					</SdfBox>

				</div>}
			</div>

			<div className="mt-16 step-container">
				<div className="text-heading-05 leading-sm font-bold">
					Step 2<br></br>
					{isDev ? "Obtain access token" : "Update your test End-user/SSO credentials to production End-user/SSO credentials"}
				</div>
			</div>

			<div className="step-container mt-4">
				<div className="min-w-0 h-full">
					<SdfBox
						shape="rounded"
						variant="bordered"
						spacing="loose"
						className="h-full"
					>
						<div>
							<div className="text-body-03">
								Use these credentials to exchange authorization code from step 1 for access token.
							</div>
							<div className="flex justify-between items-center flex-wrap mt-8">
								<div className="text-heading-04 leading-xs font-bold">1. Your credentials</div>
								<div className="flex items-center">
									<span slot="label" className="text-heading-02 font-bold mr-4">Show secret </span>
									<SdfSwitch {...(clientSecretLoading ? { disabled: true } : "")} checked={!!clientSecret} slot="action" className="cursor-pointer" onSdfChange={(event) => { onShowSecret(event) }} >
										{clientSecret ? <SdfIcon icon="action-confirm" style={{ position: 'absolute', right: '12px', fontSize: '12px' }}></SdfIcon> :
											<SdfIcon icon="action-close" style={{ position: 'absolute', left: '4px', fontSize: '12px' }}></SdfIcon>
										}
									</SdfSwitch>
								</div>
							</div>

							<div className="client-credentials ml-12 mt-6">
								<div className="client-id-container grid gap-y-3">
									{clientIdLoading ? <div>
										<div className="w-8/12"><SdfShimmer /> </div>
										<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>Client ID</div>
									</div> :
										<div className="text-md font-bold break-all">
											{clientID}
											<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>
												Client ID
											</div>
										</div>
									}
									<div className="flex copy-client-id-container items-center justify-self-end">
										{clientID ? <CopyToClipboard text={clientID}>
											<SdfTooltip positionContext="ELEMENT" tooltipId={uniqueId} trigger="click" state="info">
												<SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
												Copied
											</SdfTooltip>
										</CopyToClipboard>
											:
											<SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>}
									</div>
								</div>

								<div className="border-1 border-dark border-solid my-6"></div>

								<div className="grid client-secret-container gap-y-3">
									{clientSecretLoading ? <div>
										<SdfShimmer />
										<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>Client Secret</div>
									</div> :
										clientSecret ? <div className='text-md font-bold break-all'>
											{clientSecret}
											<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>
												Client Secret
											</div>
										</div> :
											<div>
												{[...Array(10).keys()].map((i) => <span key={i} className="rounded-full bg-action-default-300 inline-block mr-2" style={{ height: "12px", width: "12px" }}></span>)}
												<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>
													Client Secret
												</div>
											</div>
									}
									<div className="regenerate-copy-container flex items-center col-gap-2 justify-self-end">
										<SdfButton {...(!clientSecret ? { disabled: true } : "")} emphasis="tertiary" icon="action-rotate-right" onClick={() => setOpenRegenerateConfirmation(true)}>Regenerate</SdfButton>
										{clientSecret ? <CopyToClipboard text={clientSecret}>
											<SdfTooltip positionContext="ELEMENT" attachmentPoint="bottom" trigger="click" state="info">
												<SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
												Copied
											</SdfTooltip>
										</CopyToClipboard> :
											<SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>}
									</div>
								</div>
							</div>

							{openRegenerateConfirmation && <Confirmation setOpenConfirmation={setOpenRegenerateConfirmation} confirmationMessage={"Regenerating your client secret will cause your existing integration to break. Click Yes if you want to proceed."} onConfirm={regenerateCredentials} />}

							<div className="mt-12 text-heading-04 leading-xs font-bold mt-12">
								2. Security certificate
							</div>

							<div className="ml-12 mt-6">
								<ProjDetailCertAlert allCertsInfo={allCertsInfo} />
							</div>

							<div className="mt-12 text-heading-04 leading-xs font-bold">
								3. Authorization Code
							</div>

							<div className="ml-12 mt-6 mb-6">
								<SdfBox
									shape="rounded"
									variant="filled"
									spacing="loose"
									className="p-8"
								>
									{`${redirectUriPrime}?code=<authorizationCode>`}
								</SdfBox>
							</div>
						</div>
					</SdfBox>
				</div>

				<SdfBox
					shape="rounded"
					variant="filled"
					spacing="loose"
					className={`px-8 ${isDev && "h-full"}`}
				>
					<div className="text-heading-06 font-black">Open ID Connect Endpoints</div>

					<div className={`mt-8 ${isDev ? "mb-8" : "mb-12"}`} >
						<div className="mt-6 mb-1 text-heading-04 leading-xs font-bold">Authorization endpoint</div>
						<div className="text-body-02">https://accounts.adp.com/auth/oauth/v2/authorize</div>
						<div className="mt-6 mb-1 text-heading-04 leading-xs font-bold">Token endpoint</div>
						<div className="text-body-02">https://accounts.adp.com/auth/oauth/v2/token</div>
						<div className="mt-6 mb-1 text-heading-04 leading-xs font-bold">Logout endpoint</div>
						<div className="text-body-02">https://accounts.adp.com/auth/oauth/v2/logout</div>
						<div className="mt-6 mb-1 text-heading-04 leading-xs font-bold">Userinfo endpoint</div>
						<div className="text-body-02">https://api.adp.com/core/v1/userinfo</div>
					</div>

					{isDev && <SdfPromoBox
						size='sm'
						layout='vertical'
						align='left'
						variant='shadow'
						header='Get information on how ADP’s SSO uses OpenID Connect and OAuth 2.0'
						message='Gather insight into how ADP’s single-sign on uses Open ID Connect for authentication and OAuth 2.0 for authorization.'
						illustration-name={'lightbulb'}
						className='w-3/5 mb-6'
					>
						<SdfLink emphasis="none" size="sm" slot="cta" href="https://partnersupport.adp.com/hc/en-us/articles/360036870553" target="_blank">Learn more</SdfLink>
					</SdfPromoBox>}
				</SdfBox>
			</div>

			<div className="step-container mt-16">
				<div>
					<div className="text-heading-05 leading-sm font-bold">
						Step 3<br></br>
						{isDev ? "Call ADP APIs to enable SSO" : "Review ADP APIs in your scope"}
					</div>

					<SdfBox
						shape="rounded"
						variant="bordered"
						spacing="loose"
						className="p-6 pt-4 mt-4"
					>
						<div className="text-body-03">
							{isDev ? "Use supplied access token and security certificate from step 2 to call any of the following APIs in your application scope" : "Now please review your application ADP APIs."}
						</div>
						<div className="mt-12 text-right">
							<SdfLink className="mr-4 mb-2" href={useCaseDocument || firstDocument} target="_blank"> <SdfIcon icon="external-link" /> View documentation </SdfLink>
							<SdfButton emphasis="primary" onClick={() => { setSelectedTab(type === 'DEV' ? 'Development APIs' : 'Production APIs'); setEndUserApiTabActive(1) }}> View End-user/SSO APIs </SdfButton>
						</div>
					</SdfBox>
				</div>
			</div>
		</div>
	</div>);
}

export default withContext(EndUser, ProjectContext);
